import { useCallback, useEffect, useRef, useState } from "react";

import { SCENARIOS } from "../data/scenarios";
import { redirectToSouscription } from "../helpers/souscription";
import { openZendeskWidget } from "../helpers/zendesk";
import { useZeroFranchiseTracking } from "../hooks/useZeroFranchiseTracking";
import { Button } from "./Button";
import { IconButton } from "./IconButton";
import { ArrowLeftIcon } from "./icons/ArrowLeftIcon";
import { ArrowRightIcon } from "./icons/ArrowRightIcon";
import { ScenarioCard } from "./ScenarioCard";

const SCROLL_AMOUNT = 309;

export const ScenariosSection = () => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const { trackOnlineSubscription, trackRecall } = useZeroFranchiseTracking();

  const handleSubscribe = () => {
    trackOnlineSubscription("carrousel_sinistres");
    redirectToSouscription();
  };

  const handleRecall = () => {
    trackRecall("carrousel_sinistres");
    openZendeskWidget();
  };

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const update = () => {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const scrollBy = useCallback((delta: number) => {
    carouselRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  }, []);

  return (
    <section className="mx-auto flex max-w-[1600px] flex-col gap-7 bg-white py-16 md:py-24 xl:flex-row xl:items-center xl:gap-[120px] xl:py-[120px] xl:pl-[120px] xl:pr-0">
      <div className="flex flex-col px-6 md:px-12 xl:w-[388px] xl:shrink-0 xl:gap-8 xl:px-0">
        <div className="flex flex-col gap-4 text-center text-[#0D0D0D] xl:text-left">
          <h2 className="text-[23px] font-bold leading-[1.2] md:mx-auto md:max-w-[530px] md:text-[28px] xl:mx-0">
            Franchise remboursée jusqu’à 500&nbsp;€
          </h2>
          <p className="text-[16px] leading-[1.6] md:mx-auto md:max-w-[530px] xl:mx-0 xl:max-w-none xl:w-[342px]">
            Pour les sinistres du quotidien comme pour les imprévus climatiques.
          </p>
        </div>

        <div className="hidden items-center gap-4 xl:flex">
          <Button variant="primary-yellow" onClick={handleSubscribe}>
            Souscrire en ligne
          </Button>
          <Button variant="outline-dark" onClick={handleRecall}>
            Me faire rappeler
          </Button>
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-4 overflow-hidden xl:gap-6">
        <div className="relative">
          <div
            ref={carouselRef}
            className="flex w-full gap-4 overflow-x-auto pl-4 pr-4 pt-3 pb-6 md:pl-10 md:pr-10 xl:pl-3 xl:pr-[120px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            tabIndex={0}
            aria-label="Carrousel des sinistres couverts"
          >
            {SCENARIOS.map((scenario) => (
              <ScenarioCard key={scenario.id} scenario={scenario} />
            ))}
          </div>
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-y-0 left-0 hidden w-16 bg-gradient-to-r from-white to-transparent transition-opacity duration-200 xl:block ${
              canScrollLeft ? "opacity-100" : "opacity-0"
            }`}
          />
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-y-0 right-0 hidden w-32 bg-gradient-to-l from-white to-transparent transition-opacity duration-200 2xl:block ${
              canScrollRight ? "opacity-100" : "opacity-0"
            }`}
          />
        </div>

        <div className="hidden gap-3 self-center xl:flex">
          <IconButton
            variant={canScrollLeft ? "filled" : "outlined"}
            disabled={!canScrollLeft}
            ariaLabel="Sinistre précédent"
            icon={<ArrowLeftIcon size={20} />}
            onClick={() => scrollBy(-SCROLL_AMOUNT)}
          />
          <IconButton
            variant={canScrollRight ? "filled" : "outlined"}
            disabled={!canScrollRight}
            ariaLabel="Sinistre suivant"
            icon={<ArrowRightIcon size={20} />}
            onClick={() => scrollBy(SCROLL_AMOUNT)}
          />
        </div>

        <div className="flex w-full max-md:max-w-[312px] flex-col gap-4 px-4 md:flex-row md:justify-center md:px-12 xl:hidden self-center">
          <Button
            variant="primary-yellow"
            className="w-full md:w-min"
            onClick={handleSubscribe}
          >
            Souscrire en ligne
          </Button>
          <Button
            variant="outline-dark"
            className="w-full md:w-min"
            onClick={handleRecall}
          >
            Me faire rappeler
          </Button>
        </div>
      </div>
    </section>
  );
};
